import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { api } from '../services/api';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import RewriteModal from '../components/RewriteModal';

// Custom styles for Quill editor
const quillStyles = `
  .ql-container {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 14px;
    min-height: 150px;
  }
  .ql-editor {
    min-height: 150px;
    padding: 12px 15px;
  }
  .ql-editor h1 {
    font-size: 2em;
    font-weight: 700;
    line-height: 1.2;
    margin-bottom: 0.5em;
    color: #1a1a1a;
  }
  .ql-editor h2 {
    font-size: 1.5em;
    font-weight: 700;
    line-height: 1.3;
    margin-bottom: 0.5em;
    color: #1a1a1a;
  }
  .ql-editor h3 {
    font-size: 1.25em;
    font-weight: 600;
    line-height: 1.4;
    margin-bottom: 0.5em;
    color: #1a1a1a;
  }
  .ql-editor h4 {
    font-size: 1.1em;
    font-weight: 600;
    line-height: 1.4;
    margin-bottom: 0.5em;
    color: #1a1a1a;
  }
  .ql-editor p {
    font-size: 1em;
    font-weight: 400;
    line-height: 1.6;
    margin-bottom: 1em;
    color: #333;
  }
  .ql-toolbar {
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    background: #f8f9fa;
  }
  .ql-container {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }
`;

// Sanitize HTML for Quill editor — fix WordPress HTML that confuses Quill's parser
const sanitizeHtmlForQuill = (html: string): string => {
  if (!html || typeof html !== 'string') return html;

  let cleaned = html;

  // Convert WordPress [caption] shortcodes to HTML
  // [caption align="alignnone" width="1600"]<img src="..." /> Caption text[/caption]
  cleaned = cleaned.replace(
    /\[caption[^\]]*\]([\s\S]*?)\[\/caption\]/g,
    (_match, inner) => {
      const imgMatch = inner.match(/<img[^>]*>/i);
      const img = imgMatch ? imgMatch[0] : '';
      const captionText = inner.replace(/<img[^>]*>/i, '').trim();
      if (captionText) {
        return `${img}<p><em>${captionText}</em></p>`;
      }
      return img;
    }
  );

  // Strip any remaining WordPress shortcodes like [shortcode] or [/shortcode]
  cleaned = cleaned.replace(/\[\/?[a-zA-Z_-]+(?:\s[^\]]*)?\]/g, '');

  // Strip HTML comments (including WordPress block comments like <!-- wp:paragraph -->)
  cleaned = cleaned.replace(/<!--[\s\S]*?-->/g, '');

  // Remove class and id attributes that confuse Quill's block-type detection
  cleaned = cleaned.replace(/\s+(class|id)="[^"]*"/g, '');

  // Remove whitespace/newlines between block-level HTML tags.
  // Bare text nodes (whitespace) between <h2> and <p> inherit the h2 format in Quill.
  cleaned = cleaned.replace(/>\s+</g, '><');

  return cleaned.trim();
};

interface WordPressCredential {
  id: number;
  project_id: number;
  site_url: string;
  username: string;
  is_verified: boolean;
  available_cpts: CPTInfo[] | null;
  created_at: string;
  updated_at: string;
}

interface CPTInfo {
  slug: string;
  name: string;
  rest_base: string;
  description?: string;
  post_count?: number;
}

interface WordPressPost {
  id: number;
  title: string;
  slug: string;
  status: string;
  modified: string;
  link?: string;
  has_acf: boolean;
}

interface ACFFieldsResponse {
  post_id: number;
  post_title: string;
  post_slug: string;
  cpt_slug: string;
  acf_fields: Record<string, any>;
  field_count: number;
}

interface UpdateSession {
  id: number;
  project_id: number;
  cpt_slug: string;
  post_id: number;
  post_title: string | null;
  action: string;
  fields_updated: string[] | null;
  status: string;
  error_message: string | null;
  created_at: string;
}

// Find & Replace interfaces
interface FRFieldMatch {
  field_name: string;
  old_value: string;
  new_value: string;
  replacement_count: number;
  preview_before: string;
  preview_after: string;
}

interface FRPostMatch {
  post_id: number;
  post_title: string;
  cpt_slug: string;
  rest_base: string;
  fields: FRFieldMatch[];
  total_replacements: number;
}

interface FRPreviewResponse {
  total_posts_scanned: number;
  total_posts_with_matches: number;
  total_replacements: number;
  matches: FRPostMatch[];
}

interface FROperation {
  id: number;
  project_id: number;
  user_name: string | null;
  user_email: string | null;
  find_text: string;
  replace_text: string;
  case_sensitive: boolean;
  cpt_filter: string | null;
  total_posts_scanned: number;
  total_posts_modified: number;
  total_replacements: number;
  status: string;
  created_at: string;
  reverted_at: string | null;
  reverted_by_name: string | null;
}

type FRStep = 'form' | 'preview' | 'executing' | 'result' | 'history';

type Step = 'credentials' | 'cpt-select' | 'posts' | 'editor';

export default function LiveUpdatePage() {
  const { projectId: urlProjectId, liveStep } = useParams<{ projectId: string; liveStep?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  // Support both new URL structure (/projects/:projectId/agent) and legacy (?project_id=...)
  const projectId = urlProjectId || searchParams.get('project_id');

  // Step management — path segment takes priority over ?step= for forward-compat
  const VALID_STEPS: Step[] = ['credentials', 'cpt-select', 'posts', 'editor'];
  const pathStep = (liveStep && VALID_STEPS.includes(liveStep as Step)) ? (liveStep as Step) : null;
  const queryStep = (searchParams.get('step') as Step);
  const stepFromUrl: Step = pathStep || (VALID_STEPS.includes(queryStep) ? queryStep : 'credentials');
  const [step, setStepRaw] = useState<Step>(stepFromUrl);

  // Update step AND URL together so browser back/forward works; prefers path segments
  const setStep = useCallback((newStep: Step) => {
    setStepRaw(newStep);
    if (urlProjectId) {
      // Path-segment URL
      navigate(`/projects/${urlProjectId}/liveupdate/${newStep}`);
    } else {
      // Legacy query string (no project in path)
      setSearchParams(prev => {
        const params = new URLSearchParams(prev);
        params.set('step', newStep);
        return params;
      }, { replace: false });
    }
  }, [setSearchParams, navigate, urlProjectId]);

  // Sync step when browser back/forward changes the URL (path segment or query)
  useEffect(() => {
    const pStep = (liveStep && VALID_STEPS.includes(liveStep as Step)) ? (liveStep as Step) : null;
    const qStep = (searchParams.get('step') as Step);
    const newStep: Step = pStep || (VALID_STEPS.includes(qStep) ? qStep : 'credentials');
    if (newStep !== step) {
      setStepRaw(newStep);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, liveStep]);

  // One-time migration: if user landed on ?step=... with projectId in path, rewrite to path segment
  useEffect(() => {
    if (urlProjectId && !liveStep && searchParams.get('step')) {
      const qStep = searchParams.get('step') as Step;
      if (VALID_STEPS.includes(qStep)) {
        navigate(`/projects/${urlProjectId}/liveupdate/${qStep}`, { replace: true });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Credentials state
  const [credential, setCredential] = useState<WordPressCredential | null>(null);
  const [siteUrl, setSiteUrl] = useState('');
  const [username, setUsername] = useState('');
  const [appPassword, setAppPassword] = useState('');
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
    site_name?: string;
  } | null>(null);

  // CPT selection state
  const [selectedCpt, setSelectedCpt] = useState<CPTInfo | null>(null);
  const [availableCpts, setAvailableCpts] = useState<CPTInfo[]>([]);

  // Posts state
  const [posts, setPosts] = useState<WordPressPost[]>([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPosts, setSelectedPosts] = useState<number[]>([]);
  const [totalPosts, setTotalPosts] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Editor state
  const [editingPost, setEditingPost] = useState<WordPressPost | null>(null);
  const [acfFields, setAcfFields] = useState<Record<string, any>>({});
  const [originalFields, setOriginalFields] = useState<Record<string, any>>({});
  const [standardFields, setStandardFields] = useState<Record<string, string>>({});
  const [originalStandardFields, setOriginalStandardFields] = useState<Record<string, string>>({});
  const [loadingAcf, setLoadingAcf] = useState(false);
  const [savingAcf, setSavingAcf] = useState(false);
  const [savingStandard, setSavingStandard] = useState(false);
  const [editorError, setEditorError] = useState<string | null>(null);

  // Bulk update state
  const [bulkFields, setBulkFields] = useState<Record<string, string>>({});
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);

  // History state
  const [history, setHistory] = useState<UpdateSession[]>([]);
  const [showHistory, setShowHistory] = useState(false);

  // General state
  const [error, setError] = useState<string | null>(null);
  const [projectName, setProjectName] = useState<string>('');

  // Track which fields are in "code" mode vs "visual" mode
  const [codeMode, setCodeMode] = useState<Record<string, boolean>>({});

  // Rewrite modal state
  const [rewriteField, setRewriteField] = useState<string | null>(null);
  const [rewriteText, setRewriteText] = useState('');

  // Find & Replace state
  const [showFRModal, setShowFRModal] = useState(false);
  const [frStep, setFrStep] = useState<FRStep>('form');
  const [frFindText, setFrFindText] = useState('');
  const [frReplaceText, setFrReplaceText] = useState('');
  const [frCaseSensitive, setFrCaseSensitive] = useState(false);
  const [frCptFilter, setFrCptFilter] = useState<string>('');
  const [frLoading, setFrLoading] = useState(false);
  const [frPreview, setFrPreview] = useState<FRPreviewResponse | null>(null);
  const [frExcludedPosts, setFrExcludedPosts] = useState<Set<number>>(new Set());
  const [frResult, setFrResult] = useState<{ operation_id: number; total_posts_modified: number; total_replacements: number; status: string } | null>(null);
  const [frHistory, setFrHistory] = useState<FROperation[]>([]);
  const [frError, setFrError] = useState<string | null>(null);

  // Quill editor modules configuration
  const quillModules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, 4, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      ['blockquote'],
      ['link', 'image'],
      ['clean']
    ],
    clipboard: {
      matchVisual: false,
    },
  }), []);

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'align',
    'list', 'bullet',
    'indent',
    'blockquote',
    'link', 'image'
  ];

  // Load credentials and project info on mount
  useEffect(() => {
    if (projectId) {
      loadCredentials();
      loadProjectInfo();
    }
  }, [projectId]);

  const loadProjectInfo = async () => {
    if (!projectId) return;
    try {
      const response = await api.get(`/projects/${projectId}`);
      setProjectName(response.data.name || response.data.client_name || `Project #${projectId}`);
    } catch (err) {
      console.error('Failed to load project info:', err);
      setProjectName(`Project #${projectId}`);
    }
  };

  const loadCredentials = async () => {
    if (!projectId) return;

    try {
      const response = await api.get(`/liveupdate/credentials/${projectId}`);
      setCredential(response.data);
      setSiteUrl(response.data.site_url);
      setUsername(response.data.username);
      setAvailableCpts(response.data.available_cpts || []);
      setStep('cpt-select');
    } catch (err: any) {
      if (err.response?.status === 404) {
        // No credentials yet, stay on credentials step
        setStep('credentials');
      } else {
        console.error('Failed to load credentials:', err);
      }
    }
  };

  const testConnection = async () => {
    if (!siteUrl || !username || !appPassword) {
      setError('Please fill in all fields');
      return;
    }

    setTesting(true);
    setTestResult(null);
    setError(null);

    try {
      const response = await api.post('/liveupdate/credentials/test', {
        site_url: siteUrl,
        username,
        app_password: appPassword,
      });

      setTestResult(response.data);

      if (response.data.success && response.data.available_cpts) {
        setAvailableCpts(response.data.available_cpts);
      }
    } catch (err: any) {
      setTestResult({
        success: false,
        message: err.response?.data?.detail || 'Connection test failed',
      });
    } finally {
      setTesting(false);
    }
  };

  const saveCredentials = async () => {
    if (!projectId || !siteUrl || !username || !appPassword) {
      setError('Please fill in all fields and test the connection first');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const response = await api.post(`/liveupdate/credentials/${projectId}`, {
        site_url: siteUrl,
        username,
        app_password: appPassword,
      });

      setCredential(response.data);
      setAvailableCpts(response.data.available_cpts || []);
      setAppPassword(''); // Clear password from state
      setStep('cpt-select');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save credentials');
    } finally {
      setSaving(false);
    }
  };

  const deleteCredentials = async () => {
    if (!projectId || !confirm('Are you sure you want to delete these credentials?')) {
      return;
    }

    try {
      await api.delete(`/liveupdate/credentials/${projectId}`);
      setCredential(null);
      setSiteUrl('');
      setUsername('');
      setAppPassword('');
      setAvailableCpts([]);
      setStep('credentials');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete credentials');
    }
  };

  const selectCpt = async (cpt: CPTInfo) => {
    setSelectedCpt(cpt);
    setStep('posts');
    await loadPosts(cpt.slug, 1);
  };

  const loadPosts = async (cptSlug: string, page: number, search?: string) => {
    if (!projectId) return;

    setLoadingPosts(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: '50',
      });

      if (search) {
        params.append('search', search);
      }

      const response = await api.get(
        `/liveupdate/posts/${projectId}/${cptSlug}?${params.toString()}`
      );

      setPosts(response.data.posts);
      setTotalPosts(response.data.total);
      setCurrentPage(response.data.page);
      setTotalPages(response.data.total_pages);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load posts');
    } finally {
      setLoadingPosts(false);
    }
  };

  const handleSearch = () => {
    if (selectedCpt) {
      loadPosts(selectedCpt.slug, 1, searchTerm);
    }
  };

  const openEditor = async (post: WordPressPost) => {
    if (!projectId || !selectedCpt) return;

    setEditingPost(post);
    setLoadingAcf(true);
    setEditorError(null);
    setStep('editor');

    try {
      // Fetch ACF fields and standard fields in parallel
      const [acfResponse, stdResponse] = await Promise.all([
        api.get<ACFFieldsResponse>(
          `/liveupdate/posts/${projectId}/${selectedCpt.slug}/${post.id}/acf`
        ).catch(() => null),
        api.get(
          `/liveupdate/posts/${projectId}/${selectedCpt.slug}/${post.id}/standard`
        ).catch(() => null),
      ]);

      // Process ACF fields
      if (acfResponse) {
        const rawFields = acfResponse.data.acf_fields;
        const sanitizedFields: Record<string, any> = {};
        for (const [key, val] of Object.entries(rawFields)) {
          if (typeof val === 'string' && /<(p|div|table|ul|ol|h[1-6]|br|span|strong|em|blockquote)[\s>\/]/i.test(val)) {
            sanitizedFields[key] = sanitizeHtmlForQuill(val);
          } else {
            sanitizedFields[key] = val;
          }
        }
        setAcfFields(sanitizedFields);
        setOriginalFields(JSON.parse(JSON.stringify(sanitizedFields)));
      } else {
        setAcfFields({});
        setOriginalFields({});
      }

      // Process standard WP fields
      if (stdResponse) {
        const std = {
          title: stdResponse.data.title || '',
          content: stdResponse.data.content || '',
          excerpt: stdResponse.data.excerpt || '',
        };
        setStandardFields(std);
        setOriginalStandardFields({ ...std });
      } else {
        setStandardFields({ title: '', content: '', excerpt: '' });
        setOriginalStandardFields({ title: '', content: '', excerpt: '' });
      }
    } catch (err: any) {
      setEditorError(err.response?.data?.detail || 'Failed to load fields');
    } finally {
      setLoadingAcf(false);
    }
  };

  const closeEditor = () => {
    setEditingPost(null);
    setAcfFields({});
    setOriginalFields({});
    setStandardFields({});
    setOriginalStandardFields({});
    setEditorError(null);
    setStep('posts');
  };

  const handleFieldChange = (fieldName: string, value: any) => {
    setAcfFields((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const saveAcfFields = async () => {
    if (!projectId || !selectedCpt || !editingPost) return;

    // Find changed fields (using normalized comparison to ignore Quill auto-wrapping)
    const changedFields: Record<string, any> = {};
    Object.keys(acfFields).forEach((key) => {
      if (isFieldModified(key)) {
        changedFields[key] = acfFields[key];
      }
    });

    if (Object.keys(changedFields).length === 0) {
      setEditorError('No changes to save');
      return;
    }

    setSavingAcf(true);
    setEditorError(null);

    try {
      await api.put(
        `/liveupdate/posts/${projectId}/${selectedCpt.slug}/${editingPost.id}/acf`,
        {
          fields: changedFields,
        }
      );

      setOriginalFields(JSON.parse(JSON.stringify(acfFields)));
      // Refresh posts list
      await loadPosts(selectedCpt.slug, currentPage, searchTerm);
    } catch (err: any) {
      setEditorError(err.response?.data?.detail || 'Failed to save changes');
    } finally {
      setSavingAcf(false);
    }
  };

  const togglePostSelection = (postId: number) => {
    setSelectedPosts((prev) =>
      prev.includes(postId)
        ? prev.filter((id) => id !== postId)
        : [...prev, postId]
    );
  };

  const selectAllPosts = () => {
    if (selectedPosts.length === posts.length) {
      setSelectedPosts([]);
    } else {
      setSelectedPosts(posts.map((p) => p.id));
    }
  };

  const handleBulkUpdate = async () => {
    if (!projectId || !selectedCpt || selectedPosts.length === 0) return;

    const fieldsToUpdate: Record<string, any> = {};
    Object.entries(bulkFields).forEach(([key, value]) => {
      if (value.trim()) {
        fieldsToUpdate[key] = value;
      }
    });

    if (Object.keys(fieldsToUpdate).length === 0) {
      setError('Please enter at least one field value');
      return;
    }

    setBulkUpdating(true);
    setError(null);

    try {
      const response = await api.post(
        `/liveupdate/posts/${projectId}/${selectedCpt.slug}/bulk-update`,
        {
          post_ids: selectedPosts,
          fields: fieldsToUpdate,
        }
      );

      const result = response.data;
      if (result.failed_updates > 0) {
        setError(`${result.successful_updates} updated, ${result.failed_updates} failed`);
      }

      setShowBulkModal(false);
      setBulkFields({});
      setSelectedPosts([]);

      // Refresh posts list
      await loadPosts(selectedCpt.slug, currentPage, searchTerm);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Bulk update failed');
    } finally {
      setBulkUpdating(false);
    }
  };

  const loadHistory = async () => {
    if (!projectId) return;

    try {
      const response = await api.get(`/liveupdate/sessions/${projectId}?limit=50`);
      setHistory(response.data.sessions);
      setShowHistory(true);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load history');
    }
  };

  // Find & Replace handlers
  const openFRModal = () => {
    setShowFRModal(true);
    setFrStep('form');
    setFrFindText('');
    setFrReplaceText('');
    setFrCaseSensitive(false);
    setFrCptFilter('');
    setFrPreview(null);
    setFrExcludedPosts(new Set());
    setFrResult(null);
    setFrError(null);
  };

  const frRunPreview = async () => {
    if (!projectId || !frFindText) return;
    setFrLoading(true);
    setFrError(null);
    try {
      const response = await api.post(`/liveupdate/find-replace/${projectId}/preview`, {
        find_text: frFindText,
        replace_text: frReplaceText,
        case_sensitive: frCaseSensitive,
        cpt_slug: frCptFilter || null,
      });
      setFrPreview(response.data);
      setFrExcludedPosts(new Set());
      setFrStep('preview');
    } catch (err: any) {
      setFrError(err.response?.data?.detail || 'Preview failed');
    } finally {
      setFrLoading(false);
    }
  };

  const frRunExecute = async () => {
    if (!projectId || !frFindText || !frPreview) return;
    setFrStep('executing');
    setFrError(null);
    try {
      const includedPostIds = frPreview.matches
        .filter(m => !frExcludedPosts.has(m.post_id))
        .map(m => m.post_id);
      const response = await api.post(`/liveupdate/find-replace/${projectId}/execute`, {
        find_text: frFindText,
        replace_text: frReplaceText,
        case_sensitive: frCaseSensitive,
        cpt_slug: frCptFilter || null,
        post_ids: includedPostIds.length < frPreview.matches.length ? includedPostIds : null,
      });
      setFrResult(response.data);
      setFrStep('result');
    } catch (err: any) {
      setFrError(err.response?.data?.detail || 'Execute failed');
      setFrStep('preview');
    }
  };

  const frLoadHistory = async () => {
    if (!projectId) return;
    setFrLoading(true);
    setFrError(null);
    try {
      const response = await api.get(`/liveupdate/find-replace/${projectId}/history`);
      setFrHistory(response.data.operations);
      setFrStep('history');
    } catch (err: any) {
      setFrError(err.response?.data?.detail || 'Failed to load history');
    } finally {
      setFrLoading(false);
    }
  };

  const frRevert = async (operationId: number) => {
    if (!projectId || !confirm('Are you sure you want to revert this operation? Original values will be restored on WordPress.')) return;
    setFrLoading(true);
    setFrError(null);
    try {
      await api.post(`/liveupdate/find-replace/${projectId}/${operationId}/revert`);
      await frLoadHistory();
    } catch (err: any) {
      setFrError(err.response?.data?.detail || 'Revert failed');
    } finally {
      setFrLoading(false);
    }
  };

  const toggleFRPostExclusion = (postId: number) => {
    setFrExcludedPosts(prev => {
      const next = new Set(prev);
      if (next.has(postId)) {
        next.delete(postId);
      } else {
        next.add(postId);
      }
      return next;
    });
  };

  // Known CPT display names
  const getCptDisplayName = (cpt: CPTInfo) => {
    const knownCpts: Record<string, string> = {
      post: 'Blog Posts',
      page: 'Pages (Homepage, About, etc.)',
      program: 'Program Pages (CP)',
      addiction: 'Condition Pages (CP)',
      'new-hampshire': 'Service Area Pages (SAP)',
    };
    return knownCpts[cpt.slug] || cpt.name;
  };

  // Normalize HTML for comparison — strip Quill's auto-wrapping of plain text
  const normalizeForCompare = (val: any): string => {
    if (typeof val !== 'string') return JSON.stringify(val);
    // Strip trailing <p><br></p> that Quill adds to empty editors
    let s = val.replace(/<p><br><\/p>$/i, '').trim();
    // If the string is just a single <p>...</p> wrapper with no inner HTML tags,
    // extract the text to compare with the original plain text
    const singlePMatch = s.match(/^<p>(.*)<\/p>$/is);
    if (singlePMatch && !/<[a-z][\s>]/i.test(singlePMatch[1])) {
      s = singlePMatch[1];
    }
    return s;
  };

  const isFieldModified = (fieldName: string): boolean => {
    const current = acfFields[fieldName];
    const original = originalFields[fieldName];
    if (typeof current === 'string' && typeof original === 'string') {
      return normalizeForCompare(current) !== normalizeForCompare(original);
    }
    return JSON.stringify(current) !== JSON.stringify(original);
  };

  const hasStandardChanges = Object.keys(standardFields).some(
    k => standardFields[k] !== originalStandardFields[k]
  );

  const hasChanges = Object.keys(acfFields).some(k => isFieldModified(k)) || hasStandardChanges;

  const saveStandardFields = async () => {
    if (!projectId || !selectedCpt || !editingPost || !hasStandardChanges) return;

    const changedFields: Record<string, string> = {};
    Object.keys(standardFields).forEach((key) => {
      if (standardFields[key] !== originalStandardFields[key]) {
        changedFields[key] = standardFields[key];
      }
    });

    setSavingStandard(true);
    setEditorError(null);

    try {
      await api.put(
        `/liveupdate/posts/${projectId}/${selectedCpt.slug}/${editingPost.id}/standard`,
        { fields: changedFields }
      );
      setOriginalStandardFields({ ...standardFields });
      await loadPosts(selectedCpt.slug, currentPage, searchTerm);
    } catch (err: any) {
      setEditorError(err.response?.data?.detail || 'Failed to save standard fields');
    } finally {
      setSavingStandard(false);
    }
  };

  const saveAllFields = async () => {
    if (hasStandardChanges) await saveStandardFields();
    if (Object.keys(acfFields).some(k => isFieldModified(k))) await saveAcfFields();
  };

  return (
    <Layout projectId={projectId ? parseInt(projectId) : null}>
      <style>{quillStyles}</style>
      <div className="animate-fade-in">
        {/* Page Header */}
        <div className="page-header">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    if (step === 'editor') setStep('posts');
                    else if (step === 'posts') setStep('cpt-select');
                    else if (step === 'cpt-select') navigate(-1);
                    else navigate(-1);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Go back"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                </button>
                <h1 className="page-title">LIVE-Update Agent</h1>
                {projectName && (
                  <span className="px-3 py-1 bg-brand-100 text-brand-700 rounded-full text-sm font-medium">
                    {projectName}
                  </span>
                )}
              </div>
              <p className="page-subtitle">
                Connect via Application Password to browse CPTs, view ACF fields, and push content updates live
                <a href="https://drive.google.com/file/d/15zHJJcGBsPw-HMMb5MuPxNHLEEBPtLia/view" target="_blank" rel="noopener noreferrer" className="ml-3 inline-flex items-center gap-1 text-brand-600 hover:text-brand-700 font-medium">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" /></svg>
                  Video Tutorial
                </a>
                {credential && credential.site_url && (
                  <span className="ml-2 text-brand-600">
                    | Connected to {credential.site_url.replace('https://', '').replace('http://', '')}
                  </span>
                )}
              </p>
            </div>
            {credential && step !== 'credentials' && (
              <div className="flex items-center gap-3">
                <button onClick={openFRModal} className="btn-secondary">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Find &amp; Replace
                </button>
                <button onClick={loadHistory} className="btn-secondary">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  History
                </button>
                <button
                  onClick={() => setStep('credentials')}
                  className="btn-ghost text-gray-500"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  Settings
                </button>
              </div>
            )}
          </div>

          {/* Step Indicator */}
          {step !== 'credentials' && (
            <div className="mt-4 flex items-center gap-2 text-sm">
              <button
                onClick={() => setStep('cpt-select')}
                className={`px-3 py-1 rounded-full ${
                  step === 'cpt-select'
                    ? 'bg-brand-100 text-brand-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                1. Select CPT
              </button>
              <svg
                className="w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
              <button
                onClick={() => selectedCpt && setStep('posts')}
                disabled={!selectedCpt}
                className={`px-3 py-1 rounded-full ${
                  step === 'posts'
                    ? 'bg-brand-100 text-brand-700'
                    : selectedCpt
                    ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                }`}
              >
                2. Select Page
              </button>
              <svg
                className="w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
              <span
                className={`px-3 py-1 rounded-full ${
                  step === 'editor'
                    ? 'bg-brand-100 text-brand-700'
                    : 'bg-gray-50 text-gray-400'
                }`}
              >
                3. Edit Fields
              </span>
            </div>
          )}
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg flex items-start gap-3">
            <svg
              className="w-5 h-5 text-danger-500 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="text-danger-700">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-danger-500">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        )}

        {/* Step 1: Credentials */}
        {step === 'credentials' && (
          <div className="max-w-2xl mx-auto">
            <div className="card">
              <div className="card-body">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                      />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">
                      WordPress Connection
                    </h2>
                    <p className="text-gray-500">
                      Connect to your WordPress site using Application Passwords
                    </p>
                  </div>
                </div>

                {credential && (
                  <div className="mb-6 p-4 bg-success-50 border border-success-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <svg
                        className="w-5 h-5 text-success-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span className="font-medium text-success-800">
                        Connected to {credential.site_url}
                      </span>
                    </div>
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <label className="label">Site URL</label>
                    <input
                      type="url"
                      value={siteUrl}
                      onChange={(e) => setSiteUrl(e.target.value)}
                      placeholder="https://example.com"
                      className="input"
                      disabled={testing || saving}
                    />
                  </div>

                  <div>
                    <label className="label">Username</label>
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="WordPress username"
                      className="input"
                      disabled={testing || saving}
                    />
                  </div>

                  <div>
                    <label className="label">
                      Application Password
                      <span className="text-xs text-gray-400 ml-2">
                        (Create in WordPress: Users &gt; Profile &gt; Application Passwords)
                      </span>
                    </label>
                    <input
                      type="password"
                      value={appPassword}
                      onChange={(e) => setAppPassword(e.target.value)}
                      placeholder="xxxx xxxx xxxx xxxx xxxx xxxx"
                      className="input font-mono"
                      disabled={testing || saving}
                    />
                  </div>

                  {testResult && (
                    <div
                      className={`p-4 rounded-lg ${
                        testResult.success
                          ? 'bg-success-50 border border-success-200'
                          : 'bg-danger-50 border border-danger-200'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {testResult.success ? (
                          <svg
                            className="w-5 h-5 text-success-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        ) : (
                          <svg
                            className="w-5 h-5 text-danger-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        )}
                        <span
                          className={
                            testResult.success ? 'text-success-800' : 'text-danger-800'
                          }
                        >
                          {testResult.message}
                        </span>
                      </div>
                      {testResult.site_name && (
                        <p className="mt-2 text-sm text-success-700">
                          Site: {testResult.site_name}
                        </p>
                      )}
                    </div>
                  )}

                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={testConnection}
                      disabled={testing || !siteUrl || !username || !appPassword}
                      className="btn-secondary flex-1"
                    >
                      {testing ? (
                        <>
                          <span className="spinner"></span>
                          Testing...
                        </>
                      ) : (
                        <>
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                            />
                          </svg>
                          Test Connection
                        </>
                      )}
                    </button>
                    <button
                      onClick={saveCredentials}
                      disabled={saving || !testResult?.success}
                      className="btn-primary flex-1"
                    >
                      {saving ? (
                        <>
                          <span className="spinner"></span>
                          Saving...
                        </>
                      ) : (
                        <>
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          Save & Continue
                        </>
                      )}
                    </button>
                  </div>

                  {credential && (
                    <div className="pt-4 border-t border-gray-200">
                      <button onClick={deleteCredentials} className="btn-danger w-full">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                        Delete Credentials
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: CPT Selection */}
        {step === 'cpt-select' && (
          <div className="max-w-4xl mx-auto">
            <div className="card">
              <div className="card-header">
                <h2 className="text-lg font-semibold text-gray-900">
                  Select Custom Post Type
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Choose which type of pages you want to update
                </p>
              </div>
              <div className="card-body">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {availableCpts.map((cpt) => (
                    <button
                      key={cpt.slug}
                      onClick={() => selectCpt(cpt)}
                      className="p-6 border-2 border-gray-200 rounded-xl hover:border-brand-400 hover:bg-brand-50 transition-all text-left group"
                    >
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                        <svg
                          className="w-6 h-6 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                          />
                        </svg>
                      </div>
                      <h3 className="font-semibold text-gray-900 group-hover:text-brand-600">
                        {getCptDisplayName(cpt)}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        REST: {cpt.rest_base}
                      </p>
                      {cpt.description && (
                        <p className="text-xs text-gray-400 mt-2">{cpt.description}</p>
                      )}
                    </button>
                  ))}
                </div>

                {availableCpts.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    <svg
                      className="w-12 h-12 mx-auto text-gray-300 mb-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                      />
                    </svg>
                    <p className="font-medium">No Custom Post Types found</p>
                    <p className="text-sm mt-1">
                      Make sure your CPTs have &apos;show_in_rest&apos; =&gt; true
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Posts List */}
        {step === 'posts' && selectedCpt && (
          <div className="max-w-6xl mx-auto">
            <div className="card">
              <div className="card-header">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      {getCptDisplayName(selectedCpt)}
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                      {totalPosts} pages found - Click to edit
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {selectedPosts.length > 0 && (
                      <button
                        onClick={() => setShowBulkModal(true)}
                        className="btn-primary"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                        Bulk Update ({selectedPosts.length})
                      </button>
                    )}
                    <button
                      onClick={() => setStep('cpt-select')}
                      className="btn-secondary"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 19l-7-7 7-7"
                        />
                      </svg>
                      Back to CPTs
                    </button>
                  </div>
                </div>

                {/* Search Bar */}
                <div className="mt-4 flex gap-3">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      placeholder="Search pages..."
                      className="input pl-10"
                    />
                    <svg
                      className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                  <button onClick={handleSearch} className="btn-secondary">
                    Search
                  </button>
                </div>
              </div>

              <div className="card-body">
                {loadingPosts ? (
                  <div className="flex items-center justify-center py-12">
                    <span className="spinner mr-2"></span>
                    <span className="text-gray-500">Loading pages...</span>
                  </div>
                ) : (
                  <>
                    {/* Select All */}
                    <div className="mb-4 flex items-center gap-3 pb-4 border-b border-gray-200">
                      <button
                        onClick={selectAllPosts}
                        className="text-sm text-brand-600 hover:text-brand-700"
                      >
                        {selectedPosts.length === posts.length
                          ? 'Deselect All'
                          : 'Select All'}
                      </button>
                      {selectedPosts.length > 0 && (
                        <span className="text-sm text-gray-500">
                          {selectedPosts.length} selected
                        </span>
                      )}
                    </div>

                    {/* Posts Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {posts.map((post) => (
                        <div
                          key={post.id}
                          className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                            selectedPosts.includes(post.id)
                              ? 'border-brand-500 bg-brand-50'
                              : 'border-gray-200 hover:border-brand-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              checked={selectedPosts.includes(post.id)}
                              onChange={() => togglePostSelection(post.id)}
                              className="mt-1"
                              onClick={(e) => e.stopPropagation()}
                            />
                            <div
                              className="flex-1 min-w-0"
                              onClick={() => openEditor(post)}
                            >
                              <h4 className="font-medium text-gray-900 truncate">
                                {post.title}
                              </h4>
                              <p className="text-xs text-gray-500 mt-1 truncate">
                                /{post.slug}/
                              </p>
                              <div className="flex items-center gap-2 mt-2">
                                <span
                                  className={`px-2 py-0.5 rounded text-xs font-medium ${
                                    post.status === 'publish'
                                      ? 'bg-success-100 text-success-800'
                                      : 'bg-gray-100 text-gray-600'
                                  }`}
                                >
                                  {post.status}
                                </span>
                                {post.has_acf && (
                                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                    ACF
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-400 mt-2">
                                Modified:{' '}
                                {new Date(post.modified).toLocaleDateString()}
                              </p>
                              <div className="flex items-center gap-2 mt-2">
                                {post.link && (
                                  <a
                                    href={post.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="inline-flex items-center gap-1 text-xs text-green-600 hover:text-green-700"
                                  >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                    Live
                                  </a>
                                )}
                                {credential?.site_url && (
                                  <a
                                    href={`${credential.site_url.replace(/\/$/, '')}/wp-admin/post.php?post=${post.id}&action=edit`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
                                  >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                    WP Edit
                                  </a>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="mt-6 flex items-center justify-center gap-2">
                        <button
                          onClick={() =>
                            loadPosts(selectedCpt.slug, currentPage - 1, searchTerm)
                          }
                          disabled={currentPage === 1}
                          className="btn-secondary btn-sm"
                        >
                          Previous
                        </button>
                        <span className="text-sm text-gray-500">
                          Page {currentPage} of {totalPages}
                        </span>
                        <button
                          onClick={() =>
                            loadPosts(selectedCpt.slug, currentPage + 1, searchTerm)
                          }
                          disabled={currentPage === totalPages}
                          className="btn-secondary btn-sm"
                        >
                          Next
                        </button>
                      </div>
                    )}

                    {posts.length === 0 && (
                      <div className="text-center py-12 text-gray-500">
                        <p>No pages found</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step 4: ACF Editor */}
        {step === 'editor' && editingPost && (
          <div className="max-w-6xl mx-auto">
            <div className="card">
              <div className="card-header sticky top-0 bg-white z-10 border-b">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      Edit: {editingPost.title}
                    </h2>
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      <span className="text-sm text-gray-500">
                        Post ID: {editingPost.id} | /{editingPost.slug}/ | {Object.keys(acfFields).length} ACF + 3 standard fields
                      </span>
                      {editingPost.link && (
                        <a
                          href={editingPost.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-green-100 text-green-700 hover:bg-green-200 transition-colors"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                          View Live Page
                        </a>
                      )}
                      {credential?.site_url && (
                        <a
                          href={`${credential.site_url.replace(/\/$/, '')}/wp-admin/post.php?post=${editingPost.id}&action=edit`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          Edit in WordPress
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {hasChanges && (
                      <span className="text-sm text-yellow-600 bg-yellow-50 px-3 py-1 rounded-full">
                        Unsaved changes
                      </span>
                    )}
                    <button
                      onClick={saveAllFields}
                      disabled={(savingAcf || savingStandard) || !hasChanges}
                      className="btn-primary"
                    >
                      {savingAcf ? (
                        <>
                          <span className="spinner"></span>
                          {savingStandard ? 'Saving standard fields...' : 'Saving to WordPress...'}
                        </>
                      ) : (
                        <>
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          Save to WordPress
                        </>
                      )}
                    </button>
                    <button onClick={closeEditor} className="btn-secondary">
                      Back to Posts
                    </button>
                  </div>
                </div>
              </div>

              <div className="card-body p-0">
                {editorError && (
                  <div className="m-4 p-4 bg-danger-50 border border-danger-200 rounded-lg">
                    <span className="text-danger-700">{editorError}</span>
                  </div>
                )}

                {loadingAcf ? (
                  <div className="flex items-center justify-center py-12">
                    <span className="spinner mr-2"></span>
                    <span className="text-gray-500">Loading fields...</span>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {/* Standard WordPress Fields (Title, Content, Excerpt) */}
                    {(standardFields.title || standardFields.content || standardFields.excerpt) && (
                      <>
                        <div className="p-4 bg-blue-50 border-b border-blue-200">
                          <h3 className="text-sm font-semibold text-blue-800 uppercase tracking-wide">Standard WordPress Fields</h3>
                        </div>

                        {/* Title */}
                        <div className={`p-4 ${standardFields.title !== originalStandardFields.title ? 'bg-yellow-50' : 'hover:bg-gray-50'}`}>
                          <div className="flex items-start gap-4">
                            <div className="w-48 flex-shrink-0">
                              <label className="font-medium text-gray-900 text-sm block">Title</label>
                              <span className="text-xs text-gray-400 font-mono">title</span>
                              {standardFields.title !== originalStandardFields.title && (
                                <span className="block mt-1 px-2 py-0.5 rounded text-xs bg-yellow-200 text-yellow-800 w-fit">Modified</span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <input
                                type="text"
                                value={standardFields.title}
                                onChange={(e) => setStandardFields(prev => ({ ...prev, title: e.target.value }))}
                                className="input w-full"
                                placeholder="Post title..."
                              />
                            </div>
                          </div>
                        </div>

                        {/* Content */}
                        <div className={`p-4 ${standardFields.content !== originalStandardFields.content ? 'bg-yellow-50' : 'hover:bg-gray-50'}`}>
                          <div className="flex items-start gap-4">
                            <div className="w-48 flex-shrink-0">
                              <label className="font-medium text-gray-900 text-sm block">Content</label>
                              <span className="text-xs text-gray-400 font-mono">content</span>
                              {standardFields.content !== originalStandardFields.content && (
                                <span className="block mt-1 px-2 py-0.5 rounded text-xs bg-yellow-200 text-yellow-800 w-fit">Modified</span>
                              )}
                              <span className="block mt-1 text-xs text-gray-400">{standardFields.content.length} chars</span>
                              <button
                                onClick={() => setCodeMode(prev => ({ ...prev, __wp_content: !prev.__wp_content }))}
                                className={`mt-2 text-xs px-2 py-1 rounded ${codeMode.__wp_content ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-700'}`}
                              >
                                {codeMode.__wp_content ? '</> Code' : 'Visual'}
                              </button>
                              <button
                                onClick={() => {
                                  const tmp = document.createElement('div');
                                  tmp.innerHTML = standardFields.content;
                                  const plain = tmp.textContent || tmp.innerText || standardFields.content;
                                  if (plain.trim()) {
                                    setRewriteField('__wp_content');
                                    setRewriteText(plain.trim());
                                  }
                                }}
                                className="mt-1 block text-xs px-2 py-1 rounded bg-purple-100 text-purple-700 hover:bg-purple-200 transition-colors"
                              >
                                Rewrite
                              </button>
                            </div>
                            <div className="flex-1 min-w-0">
                              {codeMode.__wp_content ? (
                                <textarea
                                  value={standardFields.content}
                                  onChange={(e) => setStandardFields(prev => ({ ...prev, content: e.target.value }))}
                                  className="textarea h-64 font-mono text-sm w-full bg-gray-900 text-green-400"
                                  placeholder="Enter content..."
                                />
                              ) : (
                                <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
                                  <ReactQuill
                                    theme="snow"
                                    value={standardFields.content}
                                    onChange={(content) => setStandardFields(prev => ({ ...prev, content }))}
                                    modules={quillModules}
                                    formats={quillFormats}
                                    className="bg-white"
                                    style={{ minHeight: '200px' }}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Excerpt */}
                        <div className={`p-4 ${standardFields.excerpt !== originalStandardFields.excerpt ? 'bg-yellow-50' : 'hover:bg-gray-50'}`}>
                          <div className="flex items-start gap-4">
                            <div className="w-48 flex-shrink-0">
                              <label className="font-medium text-gray-900 text-sm block">Excerpt</label>
                              <span className="text-xs text-gray-400 font-mono">excerpt</span>
                              {standardFields.excerpt !== originalStandardFields.excerpt && (
                                <span className="block mt-1 px-2 py-0.5 rounded text-xs bg-yellow-200 text-yellow-800 w-fit">Modified</span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <textarea
                                value={standardFields.excerpt}
                                onChange={(e) => setStandardFields(prev => ({ ...prev, excerpt: e.target.value }))}
                                className="textarea h-24 w-full"
                                placeholder="Post excerpt..."
                              />
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {/* ACF Fields Section Header */}
                    {Object.keys(acfFields).length > 0 && (
                      <div className="p-4 bg-purple-50 border-b border-purple-200">
                        <h3 className="text-sm font-semibold text-purple-800 uppercase tracking-wide">ACF Fields</h3>
                      </div>
                    )}

                    {Object.keys(acfFields).length === 0 && !standardFields.title && !standardFields.content ? (
                      <div className="text-center py-12 text-gray-500">
                        <p>No editable fields found for this post</p>
                      </div>
                    ) : Object.keys(acfFields).length === 0 ? null : (
                      Object.entries(acfFields).map(([fieldName, value]) => {
                        const isModified = isFieldModified(fieldName);
                        const isNull = value === null;
                        const isArray = Array.isArray(value);
                        const isObject = typeof value === 'object' && !isArray && !isNull;
                        const isHtml = typeof value === 'string' && /<(p|div|table|ul|ol|h[1-6]|br|span|strong|em|a |img |blockquote|section|article|header|footer|nav|figure|figcaption|li|td|tr|th|thead|tbody|dl|dt|dd)[\s>\/]/i.test(value);
                        // Any string with HTML or 50+ chars gets the WYSIWYG editor + rewrite
                        const isRichContent = typeof value === 'string' && (isHtml || value.length >= 50 || value.includes('\n'));
                        const isImageField = fieldName.includes('image') && !fieldName.includes('alt');

                        return (
                          <div key={fieldName} className={`p-4 ${isModified ? 'bg-yellow-50' : 'hover:bg-gray-50'}`}>
                            <div className="flex items-start gap-4">
                              {/* Field Label */}
                              <div className="w-48 flex-shrink-0">
                                <label className="font-medium text-gray-900 text-sm block">
                                  {fieldName.replace(/_/g, ' ').replace(/cp /g, '').replace(/sap /g, '')}
                                </label>
                                <span className="text-xs text-gray-400 font-mono">{fieldName}</span>
                                {isModified && (
                                  <span className="block mt-1 px-2 py-0.5 rounded text-xs bg-yellow-200 text-yellow-800 w-fit">
                                    Modified
                                  </span>
                                )}
                                {isImageField && (
                                  <span className="block mt-1 px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800 w-fit">
                                    Image ID
                                  </span>
                                )}
                              </div>

                              {/* Field Editor */}
                              <div className="flex-1 min-w-0">
                                {isNull ? (
                                  <div className="flex items-center gap-2">
                                    <span className="text-gray-400 italic">null (empty)</span>
                                    <button
                                      onClick={() => handleFieldChange(fieldName, '')}
                                      className="text-xs text-brand-600 hover:text-brand-700"
                                    >
                                      Set value
                                    </button>
                                  </div>
                                ) : isArray ? (
                                  <div>
                                    <div className="flex items-center gap-2 mb-2">
                                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                        Array with {value.length} items
                                      </span>
                                      <button
                                        onClick={() => {
                                          const el = document.getElementById(`json-${fieldName}`);
                                          if (el) el.style.display = el.style.display === 'none' ? 'block' : 'none';
                                        }}
                                        className="text-xs text-brand-600 hover:text-brand-700"
                                      >
                                        Toggle JSON Editor
                                      </button>
                                    </div>
                                    {/* Preview items */}
                                    <div className="space-y-2 mb-2">
                                      {value.slice(0, 3).map((item: any, idx: number) => (
                                        <div key={idx} className="text-sm bg-gray-50 p-2 rounded border">
                                          <span className="text-gray-500">#{idx + 1}:</span>{' '}
                                          {typeof item === 'object'
                                            ? (item.tab_title || item.q || item.step_title || item.label || JSON.stringify(item).slice(0, 50) + '...')
                                            : String(item).slice(0, 50)}
                                        </div>
                                      ))}
                                      {value.length > 3 && (
                                        <div className="text-xs text-gray-400">...and {value.length - 3} more items</div>
                                      )}
                                    </div>
                                    <textarea
                                      id={`json-${fieldName}`}
                                      value={JSON.stringify(value, null, 2)}
                                      onChange={(e) => {
                                        try {
                                          const parsed = JSON.parse(e.target.value);
                                          handleFieldChange(fieldName, parsed);
                                        } catch {
                                          // Invalid JSON, don't update
                                        }
                                      }}
                                      className="textarea h-64 font-mono text-xs w-full"
                                      style={{ display: 'none' }}
                                    />
                                  </div>
                                ) : isObject ? (
                                  <div>
                                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded mb-2 inline-block">
                                      Object
                                    </span>
                                    <textarea
                                      value={JSON.stringify(value, null, 2)}
                                      onChange={(e) => {
                                        try {
                                          const parsed = JSON.parse(e.target.value);
                                          handleFieldChange(fieldName, parsed);
                                        } catch {
                                          // Invalid JSON
                                        }
                                      }}
                                      className="textarea h-32 font-mono text-xs w-full mt-2"
                                    />
                                  </div>
                                ) : isRichContent ? (
                                  <div>
                                    <div className="flex items-center gap-2 mb-2">
                                      <span className={`text-xs px-2 py-1 rounded ${isHtml ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                                        {isHtml ? 'HTML Content' : 'Content'}
                                      </span>
                                      <span className="text-xs text-gray-400">
                                        {String(value).length} chars
                                      </span>
                                      <button
                                        onClick={() => setCodeMode(prev => ({ ...prev, [fieldName]: !prev[fieldName] }))}
                                        className={`text-xs px-2 py-1 rounded ${codeMode[fieldName] ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-700'}`}
                                      >
                                        {codeMode[fieldName] ? '</> Code' : 'Visual'}
                                      </button>
                                      <button
                                        onClick={() => {
                                          const tmp = document.createElement('div');
                                          tmp.innerHTML = String(value);
                                          const plain = tmp.textContent || tmp.innerText || String(value);
                                          if (plain.trim()) {
                                            setRewriteField(fieldName);
                                            setRewriteText(plain.trim());
                                          }
                                        }}
                                        className="text-xs px-2 py-1 rounded bg-purple-100 text-purple-700 hover:bg-purple-200 transition-colors"
                                      >
                                        Rewrite
                                      </button>
                                    </div>
                                    {codeMode[fieldName] ? (
                                      <textarea
                                        value={String(value)}
                                        onChange={(e) => handleFieldChange(fieldName, e.target.value)}
                                        className="textarea h-48 font-mono text-sm w-full bg-gray-900 text-green-400"
                                        placeholder="Enter content..."
                                      />
                                    ) : (
                                      <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
                                        <ReactQuill
                                          theme="snow"
                                          value={String(value)}
                                          onChange={(content) => handleFieldChange(fieldName, content)}
                                          modules={quillModules}
                                          formats={quillFormats}
                                          className="bg-white"
                                          style={{ minHeight: '150px' }}
                                        />
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <input
                                    type="text"
                                    value={String(value)}
                                    onChange={(e) => handleFieldChange(fieldName, e.target.value)}
                                    className="input w-full"
                                    placeholder="Enter value..."
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>

              {/* Sticky footer with save button */}
              {(Object.keys(acfFields).length > 0 || standardFields.title || standardFields.content) && (
                <div className="card-footer sticky bottom-0 bg-white border-t flex justify-between items-center">
                  <div className="text-sm text-gray-500">
                    {Object.keys(acfFields).filter(k => isFieldModified(k)).length + Object.keys(standardFields).filter(k => standardFields[k] !== originalStandardFields[k]).length} field(s) modified
                  </div>
                  <div className="flex items-center gap-3">
                    <button onClick={closeEditor} className="btn-secondary">
                      Cancel
                    </button>
                    <button
                      onClick={saveAllFields}
                      disabled={(savingAcf || savingStandard) || !hasChanges}
                      className="btn-primary"
                    >
                      {(savingAcf || savingStandard) ? 'Saving...' : 'Save to WordPress'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Bulk Update Modal */}
        {showBulkModal && (
          <>
            <div
              className="fixed inset-0 bg-black/50 z-40"
              onClick={() => setShowBulkModal(false)}
            ></div>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto">
                <div className="p-6 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Bulk Update ACF Fields
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Apply these values to {selectedPosts.length} selected posts
                  </p>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <label className="label">Field Name</label>
                    <input
                      type="text"
                      placeholder="Enter ACF field name"
                      className="input"
                      onBlur={(e) => {
                        if (e.target.value && !bulkFields[e.target.value]) {
                          setBulkFields((prev) => ({
                            ...prev,
                            [e.target.value]: '',
                          }));
                        }
                      }}
                    />
                  </div>

                  {Object.entries(bulkFields).map(([fieldName, value]) => (
                    <div key={fieldName}>
                      <div className="flex items-center justify-between mb-1">
                        <label className="label mb-0">{fieldName}</label>
                        <button
                          onClick={() =>
                            setBulkFields((prev) => {
                              const newFields = { ...prev };
                              delete newFields[fieldName];
                              return newFields;
                            })
                          }
                          className="text-danger-500 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                      <textarea
                        value={value}
                        onChange={(e) =>
                          setBulkFields((prev) => ({
                            ...prev,
                            [fieldName]: e.target.value,
                          }))
                        }
                        className="textarea h-24"
                        placeholder="Enter value for this field"
                      />
                    </div>
                  ))}
                </div>
                <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                  <button
                    onClick={() => setShowBulkModal(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleBulkUpdate}
                    disabled={bulkUpdating || Object.keys(bulkFields).length === 0}
                    className="btn-primary"
                  >
                    {bulkUpdating ? (
                      <>
                        <span className="spinner"></span>
                        Updating...
                      </>
                    ) : (
                      <>
                        Update {selectedPosts.length} Posts
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* History Modal */}
        {showHistory && (
          <>
            <div
              className="fixed inset-0 bg-black/50 z-40"
              onClick={() => setShowHistory(false)}
            ></div>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-auto">
                <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Update History
                  </h3>
                  <button
                    onClick={() => setShowHistory(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                <div className="p-6">
                  {history.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <p>No update history yet</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {history.map((session) => (
                        <div
                          key={session.id}
                          className="p-4 border border-gray-200 rounded-lg"
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium text-gray-900">
                                {session.post_title || `Post #${session.post_id}`}
                              </p>
                              <p className="text-sm text-gray-500">
                                {session.cpt_slug} | {session.action}
                              </p>
                              {session.fields_updated && (
                                <p className="text-xs text-gray-400 mt-1">
                                  Fields: {session.fields_updated.join(', ')}
                                </p>
                              )}
                            </div>
                            <div className="text-right">
                              <span
                                className={`px-2 py-1 rounded text-xs font-medium ${
                                  session.status === 'success'
                                    ? 'bg-success-100 text-success-800'
                                    : 'bg-danger-100 text-danger-800'
                                }`}
                              >
                                {session.status}
                              </span>
                              <p className="text-xs text-gray-400 mt-2">
                                {new Date(session.created_at).toLocaleString()}
                              </p>
                            </div>
                          </div>
                          {session.error_message && (
                            <p className="text-sm text-danger-600 mt-2">
                              {session.error_message}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
        {/* Find & Replace Modal */}
        {showFRModal && (
          <>
            <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setShowFRModal(false)} />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-auto">
                {/* Modal Header */}
                <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {frStep === 'form' && 'Find & Replace'}
                      {frStep === 'preview' && 'Preview Matches'}
                      {frStep === 'executing' && 'Executing Replacements'}
                      {frStep === 'result' && 'Replace Complete'}
                      {frStep === 'history' && 'Find & Replace History'}
                    </h3>
                    {frStep === 'form' && (
                      <p className="text-sm text-gray-500 mt-1">Search and replace text across all WordPress posts and ACF fields</p>
                    )}
                  </div>
                  <button onClick={() => setShowFRModal(false)} className="text-gray-400 hover:text-gray-600">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Error banner */}
                {frError && (
                  <div className="mx-6 mt-4 p-3 bg-danger-50 border border-danger-200 rounded-lg text-sm text-danger-700 flex items-center justify-between">
                    <span>{frError}</span>
                    <button onClick={() => setFrError(null)} className="text-danger-500 ml-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}

                {/* Step: Form */}
                {frStep === 'form' && (
                  <div className="p-6 space-y-4">
                    <div>
                      <label className="label">Find Text</label>
                      <input
                        type="text"
                        value={frFindText}
                        onChange={(e) => setFrFindText(e.target.value)}
                        className="input"
                        placeholder="Enter text to find..."
                        autoFocus
                      />
                    </div>
                    <div>
                      <label className="label">Replace With</label>
                      <input
                        type="text"
                        value={frReplaceText}
                        onChange={(e) => setFrReplaceText(e.target.value)}
                        className="input"
                        placeholder="Enter replacement text..."
                      />
                    </div>
                    <div className="flex items-center gap-6">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={frCaseSensitive}
                          onChange={(e) => setFrCaseSensitive(e.target.checked)}
                          className="rounded"
                        />
                        <span className="text-sm text-gray-700">Case sensitive</span>
                      </label>
                      <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-700">CPT Filter:</label>
                        <select
                          value={frCptFilter}
                          onChange={(e) => setFrCptFilter(e.target.value)}
                          className="input py-1 text-sm w-48"
                        >
                          <option value="">All CPTs</option>
                          {availableCpts.map((cpt) => (
                            <option key={cpt.slug} value={cpt.slug}>{cpt.name} ({cpt.slug})</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
                      <button
                        onClick={frRunPreview}
                        disabled={frLoading || !frFindText}
                        className="btn-primary flex-1"
                      >
                        {frLoading ? (
                          <><span className="spinner"></span> Scanning...</>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            Preview Matches
                          </>
                        )}
                      </button>
                      <button onClick={frLoadHistory} disabled={frLoading} className="btn-secondary">
                        View History
                      </button>
                    </div>
                  </div>
                )}

                {/* Step: Preview */}
                {frStep === 'preview' && frPreview && (
                  <div className="p-6">
                    {/* Summary bar */}
                    <div className="flex items-center gap-4 mb-4 p-3 bg-brand-50 border border-brand-200 rounded-lg text-sm">
                      <span className="font-medium text-brand-800">
                        {frPreview.total_replacements} replacement{frPreview.total_replacements !== 1 ? 's' : ''} in {frPreview.total_posts_with_matches} post{frPreview.total_posts_with_matches !== 1 ? 's' : ''}
                      </span>
                      <span className="text-brand-600">|</span>
                      <span className="text-brand-600">{frPreview.total_posts_scanned} posts scanned</span>
                    </div>

                    {frPreview.matches.length === 0 ? (
                      <div className="text-center py-12 text-gray-500">
                        <p className="font-medium">No matches found</p>
                        <p className="text-sm mt-1">Try a different search term or CPT filter</p>
                        <button onClick={() => setFrStep('form')} className="btn-secondary mt-4">Back to Search</button>
                      </div>
                    ) : (
                      <>
                        {/* Scrollable match list */}
                        <div className="space-y-3 max-h-[50vh] overflow-y-auto">
                          {frPreview.matches.map((match) => {
                            const excluded = frExcludedPosts.has(match.post_id);
                            return (
                              <div key={match.post_id} className={`border rounded-lg p-4 ${excluded ? 'opacity-50 bg-gray-50' : 'bg-white'}`}>
                                <div className="flex items-start gap-3">
                                  <input
                                    type="checkbox"
                                    checked={!excluded}
                                    onChange={() => toggleFRPostExclusion(match.post_id)}
                                    className="mt-1 rounded"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-2">
                                      <span className="font-medium text-gray-900 truncate">{match.post_title}</span>
                                      <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600">{match.cpt_slug}</span>
                                      <span className="text-xs text-gray-400">ID: {match.post_id}</span>
                                      <span className="ml-auto text-xs font-medium text-brand-600">{match.total_replacements} match{match.total_replacements !== 1 ? 'es' : ''}</span>
                                    </div>
                                    {match.fields.map((field, fi) => (
                                      <div key={fi} className="mt-2 text-sm">
                                        <span className="font-mono text-xs text-gray-500 block mb-1">{field.field_name} ({field.replacement_count}x)</span>
                                        <div className="grid grid-cols-2 gap-2">
                                          <div className="p-2 bg-red-50 border border-red-200 rounded text-xs font-mono break-all">
                                            <span className="text-red-400 text-[10px] block mb-1">BEFORE</span>
                                            {field.preview_before}
                                          </div>
                                          <div className="p-2 bg-green-50 border border-green-200 rounded text-xs font-mono break-all">
                                            <span className="text-green-400 text-[10px] block mb-1">AFTER</span>
                                            {field.preview_after}
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Action buttons */}
                        <div className="flex items-center gap-3 pt-4 mt-4 border-t border-gray-200">
                          <button onClick={() => setFrStep('form')} className="btn-secondary">
                            Back to Search
                          </button>
                          <div className="flex-1" />
                          <span className="text-sm text-gray-500">
                            {frPreview.matches.length - frExcludedPosts.size} of {frPreview.matches.length} posts selected
                          </span>
                          <button
                            onClick={frRunExecute}
                            disabled={frPreview.matches.length - frExcludedPosts.size === 0}
                            className="btn-primary"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Execute Replace
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Step: Executing */}
                {frStep === 'executing' && (
                  <div className="p-12 text-center">
                    <div className="spinner mx-auto mb-4" style={{ width: 32, height: 32 }}></div>
                    <p className="font-medium text-gray-900">Executing replacements...</p>
                    <p className="text-sm text-gray-500 mt-2">Pushing updates to WordPress. This may take a moment.</p>
                  </div>
                )}

                {/* Step: Result */}
                {frStep === 'result' && frResult && (
                  <div className="p-6 text-center">
                    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-4 ${
                      frResult.status === 'completed' ? 'bg-success-100 text-success-800' :
                      frResult.status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-danger-100 text-danger-800'
                    }`}>
                      {frResult.status === 'completed' && (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                      {frResult.status === 'completed' ? 'All Replacements Successful' :
                       frResult.status === 'partial' ? 'Partial Success' : 'Failed'}
                    </div>
                    <div className="flex items-center justify-center gap-8 mb-6">
                      <div>
                        <div className="text-2xl font-bold text-gray-900">{frResult.total_posts_modified}</div>
                        <div className="text-xs text-gray-500">Posts Modified</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-gray-900">{frResult.total_replacements}</div>
                        <div className="text-xs text-gray-500">Replacements Made</div>
                      </div>
                    </div>
                    <div className="flex items-center justify-center gap-3">
                      <button onClick={() => setShowFRModal(false)} className="btn-primary">Done</button>
                      <button onClick={frLoadHistory} className="btn-secondary">View History</button>
                    </div>
                  </div>
                )}

                {/* Step: History */}
                {frStep === 'history' && (
                  <div className="p-6">
                    {frLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <span className="spinner mr-2"></span>
                        <span className="text-gray-500">Loading history...</span>
                      </div>
                    ) : frHistory.length === 0 ? (
                      <div className="text-center py-12 text-gray-500">
                        <p>No find & replace operations yet</p>
                      </div>
                    ) : (
                      <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                        {frHistory.map((op) => (
                          <div key={op.id} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex items-start justify-between">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                  <span className="font-mono text-sm bg-red-50 text-red-700 px-2 py-0.5 rounded">{op.find_text}</span>
                                  <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                  </svg>
                                  <span className="font-mono text-sm bg-green-50 text-green-700 px-2 py-0.5 rounded">{op.replace_text || '(empty)'}</span>
                                </div>
                                <div className="text-xs text-gray-500 flex items-center gap-2 flex-wrap mt-1">
                                  <span>{op.total_posts_modified} posts, {op.total_replacements} replacements</span>
                                  {op.cpt_filter && <span className="bg-gray-100 px-1.5 py-0.5 rounded">{op.cpt_filter}</span>}
                                  {op.case_sensitive && <span className="bg-gray-100 px-1.5 py-0.5 rounded">Aa</span>}
                                  <span>by {op.user_name || op.user_email || 'Unknown'}</span>
                                  <span>{new Date(op.created_at).toLocaleString()}</span>
                                </div>
                                {op.reverted_at && (
                                  <div className="text-xs text-yellow-600 mt-1">
                                    Reverted by {op.reverted_by_name || 'Unknown'} on {new Date(op.reverted_at).toLocaleString()}
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  op.status === 'completed' ? 'bg-success-100 text-success-800' :
                                  op.status === 'reverted' ? 'bg-yellow-100 text-yellow-800' :
                                  op.status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-danger-100 text-danger-800'
                                }`}>
                                  {op.status}
                                </span>
                                {op.status !== 'reverted' && op.total_posts_modified > 0 && (
                                  <button
                                    onClick={() => frRevert(op.id)}
                                    disabled={frLoading}
                                    className="text-xs px-2 py-1 rounded bg-yellow-100 text-yellow-700 hover:bg-yellow-200 transition-colors"
                                  >
                                    Revert
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-3 pt-4 mt-4 border-t border-gray-200">
                      <button onClick={() => setFrStep('form')} className="btn-secondary">Back to Search</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Rewrite Modal */}
        <RewriteModal
          isOpen={rewriteField !== null}
          selectedText={rewriteText}
          onClose={() => { setRewriteField(null); setRewriteText(''); }}
          onAccept={(rewrittenText) => {
            if (rewriteField) {
              if (rewriteField === '__wp_content') {
                // Standard WP content field
                const wrapped = rewrittenText.split('\n').filter(Boolean).map(p => `<p>${p}</p>`).join('');
                setStandardFields(prev => ({ ...prev, content: wrapped }));
              } else {
                const currentValue = String(acfFields[rewriteField] || '');
                const fieldIsHtml = /<(p|div|table|ul|ol|h[1-6]|br|span|strong|em|a |img |blockquote)[\s>\/]/i.test(currentValue);
                if (fieldIsHtml) {
                  const wrapped = rewrittenText.split('\n').filter(Boolean).map(p => `<p>${p}</p>`).join('');
                  handleFieldChange(rewriteField, wrapped);
                } else {
                  handleFieldChange(rewriteField, rewrittenText);
                }
              }
            }
            setRewriteField(null);
            setRewriteText('');
          }}
        />
      </div>
    </Layout>
  );
}
